import { sortBy, last } from "lodash";
import constants from "../../../../../../appConstants";
import {
  SpecialNeedsRenderer,
  dispatchRoutesSpecialNeedsValueGetter,
  DotRenderer,
  totalUnAckPickupsValueGetter,
} from '@dataGrid/components/cellRenderer/index';
import { routeNameCellStyling } from "@dataGrid/cellStyling";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import {
  mergeUserConfig,
  formatDateTime,
  formatDateTimeWithoutUTCConversion,
} from '@dataGrid/gridUtilities';
import { MultiSelectFilter, DateTimeFilter, StaticFilter } from '@dataGrid/components/columnFilters';
import {
  LoggedInStatusIndicator,
  ClockedInStatusIndicator,
  ActionButtonRenderer,
  DriverNameCell,
  DriverStatusIcon,
  AccruedHoursCellRenderer,
  ElapseTimeRenderer,
  OccupiedCapacityRenderer,
  ETATimeRenderer,
} from "./cellRenderer";
import { DRIVER_EVENTS, DRIVER_LOGGED_STATUS } from "../../constants";
import {
  completedVsTotalStopsFormatter,
  getTotalInTrailer,
  getTotalReturnDeliveryShipments,
  getTotalDeliveryShipments,
  getPendingDeliveryShipments,
  getCompletedDeliveryShipments,
  getNoFreightpickupsCount,
  getTimeDifferenceInMinutes,
  getStopServiceWindowByStopType,
  getWeeklyAccruedHours,
  updateGridColumnHeaderNames,
  getFilteredDriverBreakDelays,
  formatArriveAtTerminalTime,
} from "../../../utils/utils";
import { splitByDelimiter, TimeRangeDelimiter } from "../../../../../common/utils/stringUtils";
import CheckViolation from "../../../../../common/checkViolation";
import {
  hasStopDelayed,
  isDriverReadyForDispatch,
  renderTimeWithErrorIcon,
  getZoneValue,
  getArrivedAtOrEnrouteStop,
  getStopArrivalTimeForDriverGrid,
} from "../../utils";
import { getDriverStatus } from "../../utils/DriverStatus";
import { utcToZonedTime  } from "date-fns-tz";
import StyledToolTip from "../../../../../common/StyledToolTip";
import { gridShortHeaderNamesConfig } from "./constants";
import { RMAXLogger } from "../../../../../../services/LoggerService/RMAXLogger";
import { getExpectedDelayEnd } from "./cellRenderer/ETATimeRenderer";
import DriverBreakRenderer from "./cellRenderer/DriverBreakRenderer";

const doesMergedConfigHaveDuplicateColumn = (mergedConfig) => {
  // check if nested columns have duplicates
 return mergedConfig.columns.filter(c => c.children && c.children?.length !== [...new Set(c.children?.map(col=> col.headerName))].length).length
}

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration({ userProfile, featureFlags, gridTitle }) {
    const {
      enableColumnShortForm,
      enableDriverZone,
      enableWeeklyAccruedHours,
      enableRestBreakInDelay
    } = featureFlags;
    const userConfig = userProfile?.columnPref;
    const defaultConfig = {
      ...GridConfig.getDefaultConfiguration({
        enableDriverZone,
        enableWeeklyAccruedHours,
        enableRestBreakInDelay
      }),
      title: gridTitle || constants.gridNames.DispatchedDriverGrid,
    };
    const mergedConfig = mergeUserConfig({
      defaultConfig,
      userConfig,
    });

    // logging config incase duplicate columns
 
   if(doesMergedConfigHaveDuplicateColumn(mergedConfig)){
      RMAXLogger.warn('Duplicate columns created', {
        userConfig: userConfig
      });
    }
   
    return updateGridColumnHeaderNames({
      gridShortHeaderNamesConfig,
      columnConfig: mergedConfig,
      enableColumnShortForm,
    });
  }

  static getUnReadMessageValue = (props) => {
    return props.data.unReadCount;
  };

  static totalPiecesValueGetter = (params) => {
    const { stops } = params.data;
    return getTotalInTrailer(stops, "pieces");
  };

  static totalReturnsValueGetter = (params) => {
    const { stops } = params.data;
    return getTotalReturnDeliveryShipments(stops);
  };

  static totalDeliveryShipments = (params) => {
    const { stops } = params.data;
    return getTotalDeliveryShipments(stops);
  };

  static totalPendingDeliveryShipments = (params) => {
    const { stops } = params.data;
    return getPendingDeliveryShipments(stops);
  };

  static totalCompletedDeliveryShipments = (params) => {
    const { stops } = params.data;
    return getCompletedDeliveryShipments(stops);
  };

  static totalNoFreightpickups = (params) => {
    const { stops } = params.data;
    return getNoFreightpickupsCount(stops);
  };

  static getValue = (params) => {
    return params.data[params.column.colId] || 0;
  };

  static getCloseTime = (params) => {
    const selectedStop = getArrivedAtOrEnrouteStop(params);
    if (!selectedStop) {
      return null;
    }
    const timeWindow = getStopServiceWindowByStopType(selectedStop);
    if (!timeWindow) {
      return null;
    }
    const latestCloseTime = [];
    splitByDelimiter(timeWindow).forEach(window => {
      latestCloseTime.push(splitByDelimiter(window, TimeRangeDelimiter)[1]);
    });
    // Sort times and get the latest one
    return last(sortBy(latestCloseTime));
  };

  static renderCloseTime(params) {   
    if (params.value) {
      const selectedStop = getArrivedAtOrEnrouteStop(params);
      const hasDelayed = hasStopDelayed(selectedStop);
      return (
        <StyledToolTip title={hasDelayed ? 'Arriving Late' : params.value}>
          {renderTimeWithErrorIcon(params.value, hasDelayed)}
        </StyledToolTip>
      );
    }
    return null;
  }

  static getETAValue = (props) => {
    const { driverStatus } = getDriverStatus(props, false);
    if (DRIVER_EVENTS.ReturnToTerminal === driverStatus) {
      return formatArriveAtTerminalTime(props.data.arriveAtTerminalTime);
    }else if (DRIVER_EVENTS.LunchBreak === driverStatus) {
      return getExpectedDelayEnd(props,DRIVER_EVENTS.LunchBreak );
    } else if (DRIVER_EVENTS.RestBreak === driverStatus) {
      return getExpectedDelayEnd(props,DRIVER_EVENTS.RestBreak)
    } else {
      const selectedStop = getArrivedAtOrEnrouteStop(props);
      if (selectedStop) {
        const arrivalTime = getStopArrivalTimeForDriverGrid(driverStatus, selectedStop);
      return arrivalTime || null
    }
  }
    
    return null;
  }

  static getDriverBreak = (delayType) => (params) => {
    return getFilteredDriverBreakDelays(params.data.delays, delayType)
      .map((delay) => {
        return `${delay.startTime} - ${delay.endTime ?? ''}`;
      })
  }

  static renderElapseTime(props, terminalOffset) {
    const { lastActionTime } = props.data;
    const timeArr = lastActionTime?.split(":");

    if (!lastActionTime?.length) {
      return null;
    }

    const today = utcToZonedTime(new Date(), terminalOffset);
    const timeDiff = getTimeDifferenceInMinutes(
      new Date(0, 0, 0, timeArr[0], timeArr[1]),
      new Date(0, 0, 0, today.getHours(), today.getMinutes())
    );
    return timeDiff;
  }

  static getWeeklyAccruedHours(props) {
    const dailyAccruedHours = props.context.getDailyAccruedHours(
      props.data.driverId
    );
    return getWeeklyAccruedHours(dailyAccruedHours, props.data.accruedHours);
  }

  static messageCell(props) {
    if (!props.data?.dispatchId) {
      return <></>;
    }

    const hasUnreadMessageCount = props.data.unReadCount > 0;
    let outlinedStyle = hasUnreadMessageCount
      ? "dispatch-message dispatch-new-message"
      : "dispatch-message";
    return (
      <div className="dispaly-message">
        <MailOutlineIcon
          className={outlinedStyle}
          onClickCapture={(event) => {
            event.stopPropagation();
            props.api.runCommand("openMessage", { routeData: props.data })
          }}
        />
        {hasUnreadMessageCount ? (
          <span className="dispatch-new-message-number">
            {props.data.unReadCount}
          </span>
        ) : null}
      </div>
    );
  }

  static stopsValueGetter(params) {
    return completedVsTotalStopsFormatter(params.data);
  }

  static DriverStatusRenderer(params) {
    const { driverClockedInTime, isCompanyDriver } = params.data;
    if (!isDriverReadyForDispatch({ driverClockedInTime, isCompanyDriver ,terminalOffset:params.context.terminalOffset})) {
      return <ClockedInStatusIndicator {...params} />;
    } else if (
      params?.data?.loggedInStatus === DRIVER_LOGGED_STATUS.loggedOut
    ) {
      return <LoggedInStatusIndicator {...params} />;
    }
    return <DotRenderer {...params} />;
  }

  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static getOccupiedCapacity(params) {
    const numberNullCheckArray = [null, undefined, ""];
    if (
      !numberNullCheckArray.includes(params.data.occupiedPse) &&
      !numberNullCheckArray.includes(params.data.trailer.maxSkid)
    ) {
      return `${params.data.occupiedPse}/${params.data.trailer.maxSkid}`;
    }
    return "";
  }

  static totalWeightValueGetter(params) {
    const { stops } = params.data;
    return getTotalInTrailer(stops, "weight");
  }

  static totalHUValueGetter(params) {
    const { stops } = params.data;
    return getTotalInTrailer(stops, "hu");
  }

  static getDefaultConfiguration({
    enableDriverZone,
    enableWeeklyAccruedHours,
    enableRestBreakInDelay
  }) {
    const defaultConfig = {
      title: constants.gridNames.DispatchedDriverGrid,
      keyColumn: "routeId",
      rowSelection: "multiple",
      rowDeselection: true,
      rowClassRules: {
        violationRowBGColor: (params) =>
          Boolean(
            CheckViolation.checkDriverHasDelayStops({
              data: { stops: params.data.stops },
            })
          ),
      },
      defaultColDef: {
        suppressSpanHeaderHeight: true, // to avoid the header height issue for header with no children
        cellDataType: false,
      },
      columns: [
        {
          headerName: "",
          minWidth: constants.columnWidth.VIOLATION_DOT,
          maxWidth: constants.columnWidth.VIOLATION_DOT,
          field: "",
          colId: "driverLoginStatus",
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          cellClass: ["gridCell-centerAlign", "gridCell-dot"],
          filter: false,
          cellRenderer: GridConfig.DriverStatusRenderer,
          isInvisibleInShowHide: true,
          gridTitle: constants.gridNames.DispatchedDriverGrid,
        },
        {
          headerName: "Action",
          headerTooltip: "Assign/UnAssign Pickup",
          minWidth: constants.columnWidth.ASSIGN_ACTION,
          cellClass: "gridCell-leftAlign",
          colId: "actionCol",
          cellRenderer: ActionButtonRenderer,
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          filter: false,
          sortable: false,
        },
        {
          headerName: "Route",
          headerTooltip: "Route Name",
          field: "routeName",
          minWidth: constants.columnWidth.ROUTE_NAME,
          cellClass: "gridCell-leftAlign",
          cellStyle: routeNameCellStyling,
          filter: MultiSelectFilter,
        },
        {
          headerName: "Driver Number",
          headerTooltip: "Driver Number",
          field: "driverNumber",
          minWidth: constants.columnWidth.PICKUP_NUMBER,
          cellClass: "gridCell-leftAlign",
          pinned: "left",
          lockPosition: true,
          lockPinned: true,
          filter: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING,
          },
        },
        {
          headerName: "Zone",
          headerTooltip: "Zone",
          colId: "zone",
          valueGetter: getZoneValue,
          minWidth: constants.columnWidth.DISPATCH_ZONE,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Acc Hrs",
          headerTooltip: "Accrued Hours (Today)",
          colId: "accruedHours",
          field: "accruedHours",
          valueGetter: (props) =>
            props.context.getDailyAccruedHours(props.data.driverId),
          cellRenderer: AccruedHoursCellRenderer,
          minWidth: constants.columnWidth.ACC_HRS,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Weekly Acc Hrs",
          headerTooltip: "Weekly Accrued Hours",
          field: "weeklyAccruedHours",
          colId: "weeklyAccruedHours",
          valueGetter: GridConfig.getWeeklyAccruedHours,
          cellRenderer: AccruedHoursCellRenderer,
          minWidth: constants.columnWidth.WEEKLY_ACC_HRS,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          hide: true,
        },
        {
          headerName: "Driver",
          field: "driverName",
          cellRenderer: (props) => (
            <DriverNameCell
              {...props}
              terminalOffset={props.context.terminalOffset}
            />
          ),
          headerTooltip: "Driver Name",
          minWidth: constants.columnWidth.DISPATCH_DRIVER_NAME,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          filterParams:{
            width: constants.columnWidth.W_300
          }
        },
        {
          headerName: "Message",
          cellRenderer: GridConfig.messageCell,
          headerTooltip: "Message",
          valueGetter: GridConfig.getUnReadMessageValue,
          minWidth: constants.columnWidth.DRIVER_INFO_SMALL,
          cellClass: "gridCell-leftAlign",
          filter: false,
          sortable: false,
          field: "msg",
        },
        {
          headerName: "Driver Status",
          field: "currentDriverStatus",
          cellRenderer: DriverStatusIcon,
          valueGetter: getDriverStatus,
          tooltipValueGetter: (params) => params.value,
          headerTooltip: "Current Driver Status",
          minWidth: constants.columnWidth.DISPATCH_DRIVER_STATUS,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          filterParams:{
            width: constants.columnWidth.W_300
          },
        },
        {
          headerName: "Close Time",
          valueGetter : GridConfig.getCloseTime,
          cellRenderer: GridConfig.renderCloseTime,
          headerTooltip: "Close Time",
          minWidth: constants.columnWidth.W_110,
          cellClass: "gridCell-leftAlign",
          colId: "closeTime",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue : true,
          }
        },
        {
          headerName: "ETA",
          valueGetter : GridConfig.getETAValue,
          cellRenderer: ETATimeRenderer,
          headerTooltip: "ETA",
          minWidth: constants.columnWidth.ETA_TIME,
          cellClass: "gridCell-leftAlign",
          colId: "eta",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue : true,
          },
        },
        {
          headerName: "Lunch Break",
          valueGetter: GridConfig.getDriverBreak(constants.DELAY_STOP_TYPES.LUNCH_BREAK),
          cellRenderer: DriverBreakRenderer,
          headerTooltip: "Lunch Break",
          minWidth: constants.columnWidth.W_140,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue : true,
          },
        },
        {
          headerName: "Rest Break",
          colId:"restBreak",
          valueGetter: GridConfig.getDriverBreak(constants.DELAY_STOP_TYPES.REST_BREAK),
          cellRenderer: DriverBreakRenderer,
          headerTooltip: "Rest Break",
          minWidth: constants.columnWidth.W_140,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue : true,
          },
        },
        {
          headerName: "EST GateIn",
          field: "arriveAtTerminalTime",
          valueFormatter: (params) => formatArriveAtTerminalTime(params.value),
          headerTooltip: "Estimated Time of Arrival at Terminal",
          minWidth: constants.columnWidth.W_140,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
        {
          headerName: "Last Active Time",
          field: "lastActionTime",
          headerTooltip: "Last Active Time",
          minWidth: constants.columnWidth.LAST_ACTIVE_TIME,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
        {
          headerName: "Elapsed Time",
          field: "elapsedTime",
          valueGetter: (props) =>
            GridConfig.renderElapseTime(props, props.context.terminalOffset),
          cellRenderer: ElapseTimeRenderer,
          headerTooltip: "Elapsed Time",
          minWidth: constants.columnWidth.ELAPSE_TIME,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "LCT",
          headerTooltip: "Last connected time",
          field: "lct",
          valueGetter:(params) => params.context.getLCT(params.data.driverId),
          minWidth: constants.columnWidth.W_85,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
          },
        },
        {
          headerName: "Unoccupied Trailer Space",
          field: "TrailerCapacity",
          headerTooltip: "Unoccupied Trailer Space",
          minWidth: constants.columnWidth.UNOCC_TRAILER_SPACE,
          cellClass: "gridCell-leftAlign",
          hide: true,
          filter: MultiSelectFilter,
        },
        {
          headerName: "Equip Type",
          field: "trailerName",
          minWidth: constants.columnWidth.TRAILER_TYPE,
          headerTooltip: "Trailer Type",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Stops",
          minWidth: constants.columnWidth.W_90,
          headerTooltip: "Completed Stops / Total Stops",
          cellClass: "gridCell-leftAlign",
          valueGetter: GridConfig.stopsValueGetter,
          filter: MultiSelectFilter,
        },
        {
          headerName: "Deliveries",
          headerClass: "groupHeaderParentDelivery",
          minWidth: constants.columnWidth.SPECIAL_INSTRUCTION,
          headerTooltip: "Deliveries",
          marryChildren: true,
          groupId: 'Deliveries',
          children: [
            {
              headerClass: "groupHeaderChildren",
              headerName: "Complete",
              headerTooltip: "Completed Deliveries",
              valueGetter: GridConfig.totalCompletedDeliveryShipments,
              minWidth: constants.columnWidth.W_110,
              filter: MultiSelectFilter,
              field: "compDel",
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "Pending",
              headerTooltip: "Pending Deliveries",
              valueGetter: GridConfig.totalPendingDeliveryShipments,
              minWidth: constants.columnWidth.W_100,
              filter: MultiSelectFilter,
              field: "pendingDel",
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "Return",
              headerTooltip: "Return Deliveries",
              minWidth: constants.columnWidth.W_95,
              valueGetter: GridConfig.totalReturnsValueGetter,
              filter: MultiSelectFilter,
              field: "returnDel",
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "Total",
              headerTooltip: "Total Deliveries",
              valueGetter: GridConfig.totalDeliveryShipments,
              minWidth: constants.columnWidth.W_85,
              filter: MultiSelectFilter,
              field: "totalDel",
            },
          ],
        },
        {
          headerName: "Pickups",
          headerClass: "groupHeaderParentPickup",
          marryChildren: true,
          groupId: 'Pickups',
          children: [
            {
              headerClass: "groupHeaderChildren",
              headerName: "Complete",
              headerTooltip: "Completed Pickups",
              field: "completedPickups",
              valueGetter: GridConfig.getValue,
              minWidth: constants.columnWidth.W_110,
              filter: MultiSelectFilter,
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "UnAck",
              headerTooltip: "Unacknowledged Pickups",
              field: "unAcknowledgedPickups",
              minWidth: constants.columnWidth.W_90,
              valueGetter: totalUnAckPickupsValueGetter,
              filter: MultiSelectFilter,
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "Pending",
              headerTooltip: "Pending Pickups",
              field: "pendingPickups",
              valueGetter: GridConfig.getValue,
              minWidth: constants.columnWidth.W_100,
              filter: MultiSelectFilter,
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "No Freight",
              headerTooltip: "No Freight Pickups",
              valueGetter: GridConfig.totalNoFreightpickups,
              minWidth: constants.columnWidth.W_115,
              filter: MultiSelectFilter,
              field: "noFreightPickups",
            },
            {
              headerClass: "groupHeaderChildren",
              headerName: "Total",
              headerTooltip: "Total Pickups",
              field: "totalPickups",
              valueGetter: GridConfig.getValue,
              minWidth: constants.columnWidth.W_85,
              filter: MultiSelectFilter,
            },
          ],
          minWidth: constants.columnWidth.SPECIAL_INSTRUCTION,
          headerTooltip: "Pickups",
          cellClass: "gridCell-centerAlign",
        },
        {
          headerName: "Spl Needs",
          minWidth: constants.columnWidth.SPL_NEED,
          valueGetter: dispatchRoutesSpecialNeedsValueGetter,
          headerTooltip: "Special Needs",
          cellClass: "gridCell-leftAlign",
          cellRenderer: SpecialNeedsRenderer,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption,
          },
          filter: StaticFilter,
        },
        {
          headerName: "Gateout",
          field: "gateOutTime",
          minWidth: constants.columnWidth.W_95,
          headerTooltip: "Gateout Time",
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
        {
          headerName: "Proj MTY",
          field: "estimatedEmptyTime",
          minWidth: constants.columnWidth.W_100,
          headerTooltip: "Projected Empty Time",
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
          },
        },
        {
          headerName: "Total HU",
          minWidth: constants.columnWidth.TOTAL_HU,
          valueGetter: GridConfig.totalHUValueGetter,
          headerTooltip: "Total Handling Unit",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          field: "totalHU",
        },
        {
          headerName: "Total Weight",
          minWidth: constants.columnWidth.WEIGHT,
          headerTooltip: "Total Weight",
          valueGetter: GridConfig.totalWeightValueGetter,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          field: "totalWeight",
        },
        {
          headerName: "Total Pieces",
          minWidth: constants.columnWidth.TOTAL_PIECES,
          headerTooltip: "Total Pieces",
          valueGetter: GridConfig.totalPiecesValueGetter,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          field: "totalPcs",
        },
        {
          headerName: "Driver Type",
          minWidth: constants.columnWidth.DRIVER_TYPE,
          hide: true,
          headerTooltip: "Driver Type",
          field: "driverType",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Dispatched Time",
          minWidth: constants.columnWidth.DATE_TIME,
          headerTooltip: "Dispatched Time",
          field: "driverDispatchTime",
          valueFormatter: formatDateTimeWithoutUTCConversion,
          hide: true,
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME,
          },
        },
        {
          headerName: "ClockedIn Time",
          minWidth: constants.columnWidth.DATE_TIME,
          headerTooltip: "ClockedIn Time",
          valueFormatter: formatDateTime,
          hide: true,
          field: "driverClockedInTime",
          cellClass: "gridCell-leftAlign",
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME,
          },
        },
        {
          headerName: "Occ.Capacity",
          minWidth: constants.columnWidth.OCC_PSE,
          headerTooltip: "Occupied Capacity",
          valueGetter: GridConfig.getOccupiedCapacity,
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
          field: "occCapacity",
          cellRenderer: OccupiedCapacityRenderer,
        },
        {
          headerName: "Trailer#",
          field: "trailerNumber",
          minWidth: constants.columnWidth.W_200,  
          headerTooltip: "Trailer Number",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Manifest#",
          field: "manifestNumber",
          minWidth: constants.columnWidth.W_200,
          headerTooltip: "Manifest Number",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
        {
          headerName: "Tractor#",
          field: "tractorNumber",
          minWidth: constants.columnWidth.PICKUP_NUMBER,
          headerTooltip: "Tractor Number",
          cellClass: "gridCell-leftAlign",
          filter: MultiSelectFilter,
        },
      ],
    };
    const columns = defaultConfig.columns.filter(column => {
      switch (column.colId) {
        case "zone":
          return enableDriverZone;
        case "weeklyAccruedHours":
          return enableWeeklyAccruedHours;
        case "restBreak":
          return enableRestBreakInDelay;
        default:
          return true;
      }
    });
    return {...defaultConfig, columns};
  }
}
